import Vue from 'vue'
import Vuex from 'vuex'
import toast from '@/store/toast'
import routing from '@/store/routing'
import order, { OrderStatus } from '@/store/order'
import restaurant from '@/store/restaurant'
import user from '@/store/user'
import dialog from '@/store/dialog'
import menu from '@/store/menu'
import booking from '@/store/booking'
import routes from '@/router/routes'

Vue.use(Vuex)

export function createStore() {
  return new Vuex.Store({
    state: {
      platform: window.localStorage.getItem('app/platform'),
      framed: window.sessionStorage.getItem('app/framed') === '1',
      persisted_tokens: JSON.parse(localStorage.getItem('app/persisted-tokens')) || [],
      offline: navigator.onLine === false,
      modal_open: false,
      navbar_loading: false,
      navbar_height: 0
    },
    mutations: {
      SET_OFFLINE(state, value) {
        state.offline = value
      },

      SET_MODAL_OPEN(state, value) {
        state.modal_open = value
      },

      SET_NAVBAR_LOADING_STATUS(state, value) {
        state.navbar_loading = value
      },

      SET_NAVBAR_HEIGHT(state, value) {
        state.navbar_height = value
      },

      REMEMBER_TOKENS(state, { orderToken, bookingToken }) {
        const existingTokens = state.persisted_tokens
        const existingEntry = existingTokens.find(entry => {
          return (
            (orderToken && entry.order_token === orderToken) ||
            (bookingToken && entry.booking_token === bookingToken)
          )
        })

        if (existingEntry) {
          existingEntry.booking_token = bookingToken || existingEntry.booking_token
          existingEntry.order_token = orderToken || existingEntry.order_token
          localStorage.setItem('app/persisted-tokens', JSON.stringify(existingTokens))
        } else {
          existingTokens.push({
            order_token: orderToken,
            booking_token: bookingToken
          })
          localStorage.setItem('app/persisted-tokens', JSON.stringify(existingTokens))
        }
      },

      FORGET_TOKENS(state, token) {
        const existingTokens = state.persisted_tokens
        const indexOfExistingToken = existingTokens.reduce((acc, entry, index) => {
          if (entry.booking_token === token || entry.order_token === token) {
            acc = index
          }
          return acc
        }, -1)

        if (indexOfExistingToken !== -1) {
          existingTokens.splice(indexOfExistingToken, 1)
          localStorage.setItem('app/persisted-tokens', JSON.stringify(existingTokens))
        }
      }
    },
    actions: {
      SET_OFFLINE({ commit }, value) {
        commit('SET_OFFLINE', value)
      },

      SET_MODAL_OPEN({ commit }, value) {
        commit('SET_MODAL_OPEN', value)
      },

      SET_NAVBAR_LOADING_STATUS({ commit }, value) {
        commit('SET_NAVBAR_LOADING_STATUS', value)
      },

      SET_NAVBAR_HEIGHT({ commit }, value) {
        commit('SET_NAVBAR_HEIGHT', value)
      },

      async FETCH_PERSISTED_TOKENS({ state, dispatch }) {
        const requests = []

        state.persisted_tokens.forEach(({ booking_token, order_token }) => {
          if (booking_token) {
            requests.push(dispatch('booking/FETCH', booking_token, { root: true }))
          }
          if (order_token) {
            requests.push(dispatch('order/FETCH', order_token, { root: true }))
          }
        })

        return Promise.all(requests)
      },

      REMEMBER_TOKENS({ commit }, entry) {
        commit('REMEMBER_TOKENS', entry)
      },

      FORGET_TOKENS({ commit }, bookingOrOrderToken) {
        commit('FORGET_TOKENS', bookingOrOrderToken)
      },

      SHOW_TABLE_PROMPT({ rootState, dispatch }, { router }) {
        const orders = rootState.order.orders
        const seatedTableOrderTokens = Object.keys(orders).filter(orderToken => {
          const order = orders[orderToken]
          const seated = !isNaN(parseInt(order.table_description, 10))
          const open = order.status === OrderStatus.OPEN

          return seated && open
        })

        // Go straight to the table if exactly one
        if (seatedTableOrderTokens.length === 1) {
          router.push({
            name: routes.TABLE_INTRO,
            params: { orderToken: seatedTableOrderTokens[0] }
          })
          return
        }

        return dispatch('dialog/PROMPT', {
          props: {
            heading: 'Gå til bord',
            body: 'Indtast koden fra bordets bon.',
            placeholder: 'Indtast bordkoden',
            asyncValidator: async payload => {
              const orderToken = payload.input

              try {
                await dispatch('order/FETCH', orderToken)
                dispatch('dialog/HIDE', orderToken)

                router.push({
                  name: routes.TABLE_INTRO,
                  params: { orderToken }
                })
              } catch (error) {
                if (error.status === 404) {
                  dispatch('toast/ERROR', 'Vi kunne ikke finde bordet — Prøv igen.')
                } else {
                  dispatch('toast/ERROR', error.message)
                }
              }
            }
          }
        })
      }
    },
    getters: {},
    modules: {
      restaurant,
      toast,
      routing,
      order,
      user,
      dialog,
      menu,
      booking
    }
  })
}
